import { CatalogResponse, Product } from '@/webapi/use-catalog-api';

/* eslint-disable no-bitwise -- portable Roaring decoding requires bit masks and unsigned shifts. */

const RELATION_FORMAT_CSR_UINT16 = 0;
const RELATION_FORMAT_CSR_UINT32 = 1;
const RELATION_FORMAT_ROARING = 2;

interface PackedRelations {
  idsByValue: Map<string, number>;
  data: Uint8Array;
  offsets: Uint32Array;
  formats: Uint8Array;
}

export interface CatalogRelationPage {
  productOffset: number;
  productCount: number;
  tags: PackedRelations;
  collections: PackedRelations;
}

export interface CatalogRelations {
  pages: CatalogRelationPage[];
}

export function decodeCatalogRelationPage(
  response: CatalogResponse,
  productOffset: number,
): CatalogRelationPage | undefined {
  if (!response.productTagRelationOffsets) {
    return undefined;
  }

  return {
    productOffset,
    productCount: response.products?.length || 0,
    tags: decodeRelations(
      response.productTagDictionary,
      response.productTagRelationData,
      response.productTagRelationOffsets,
      response.productTagRelationFormats,
    ),
    collections: decodeRelations(
      response.productCollectionDictionary,
      response.productCollectionRelationData,
      response.productCollectionRelationOffsets,
      response.productCollectionRelationFormats,
    ),
  };
}

export function productHasTag(
  product: Product,
  productIndex: number,
  relations: CatalogRelations | undefined,
  tag: string,
) {
  return (
    productHasRelation(relations, productIndex, tag, `tag`) ??
    product.tags?.includes(tag) ??
    false
  );
}

export function productBelongsToCollection(
  product: Product,
  productIndex: number,
  relations: CatalogRelations | undefined,
  collectionHandle: string,
) {
  return (
    productHasRelation(
      relations,
      productIndex,
      collectionHandle,
      `collection`,
    ) ??
    product.collections?.includes(collectionHandle) ??
    false
  );
}

export function findFirstProductForTag(
  products: Product[],
  relations: CatalogRelations | undefined,
  tag: string,
) {
  const index = findFirstProductIndex(relations, tag, `tag`);
  return products.find((product, productIndex) => {
    const page = findRelationPage(relations, productIndex);
    return page ? productIndex === index : product.tags?.includes(tag);
  });
}

export function findFirstProductForCollection(
  products: Product[],
  relations: CatalogRelations | undefined,
  collectionHandle: string,
) {
  const index = findFirstProductIndex(
    relations,
    collectionHandle,
    `collection`,
  );
  return products.find((product, productIndex) => {
    const page = findRelationPage(relations, productIndex);
    return page
      ? productIndex === index
      : product.collections?.includes(collectionHandle);
  });
}

function decodeRelations(
  dictionary?: string[],
  data?: Uint8Array | string,
  offsets?: Uint8Array | string,
  formats?: Uint8Array | string,
): PackedRelations {
  return {
    idsByValue: new Map(
      (dictionary || []).map((value, index) => [value, index]),
    ),
    data: toBytes(data),
    offsets: decodePackedUint32(toBytes(offsets)),
    formats: toBytes(formats),
  };
}

function toBytes(value?: Uint8Array | string) {
  if (!value) {
    return new Uint8Array();
  }
  if (value instanceof Uint8Array) {
    return value;
  }
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function decodePackedUint32(bytes: Uint8Array) {
  if (bytes.length % 4 !== 0) {
    throw new Error(`Invalid packed catalog relation offsets`);
  }
  const values = new Uint32Array(bytes.length / 4);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let index = 0; index < values.length; index += 1) {
    values[index] = view.getUint32(index * 4, true);
  }
  return values;
}

function productHasRelation(
  relations: CatalogRelations | undefined,
  productIndex: number,
  value: string,
  kind: 'tag' | 'collection',
) {
  const page = findRelationPage(relations, productIndex);
  if (!page) {
    return undefined;
  }

  const packed = kind === `tag` ? page.tags : page.collections;
  const relationId = packed.idsByValue.get(value);
  if (relationId === undefined) {
    return false;
  }
  return relationContains(
    packed,
    relationId,
    productIndex - page.productOffset,
  );
}

function findRelationPage(
  relations: CatalogRelations | undefined,
  productIndex: number,
) {
  return relations?.pages.find(
    (candidate) =>
      productIndex >= candidate.productOffset &&
      productIndex < candidate.productOffset + candidate.productCount,
  );
}

function findFirstProductIndex(
  relations: CatalogRelations | undefined,
  value: string,
  kind: 'tag' | 'collection',
) {
  let result: number | undefined;
  relations?.pages.some((page) => {
    const packed = kind === `tag` ? page.tags : page.collections;
    const relationId = packed.idsByValue.get(value);
    if (relationId === undefined) {
      return false;
    }
    const first = relationFirst(packed, relationId);
    if (first !== undefined) {
      result = page.productOffset + first;
      return true;
    }
    return false;
  });
  return result;
}

function relationContains(
  relations: PackedRelations,
  relationId: number,
  target: number,
) {
  const payload = relationPayload(relations, relationId);
  switch (relations.formats[relationId]) {
    case RELATION_FORMAT_CSR_UINT16:
      return sortedBytesInclude(payload, 2, target);
    case RELATION_FORMAT_CSR_UINT32:
      return sortedBytesInclude(payload, 4, target);
    case RELATION_FORMAT_ROARING:
      return roaringContains(payload, target);
    default:
      throw new Error(`Unknown catalog relation encoding`);
  }
}

function relationFirst(relations: PackedRelations, relationId: number) {
  const payload = relationPayload(relations, relationId);
  if (!payload.length) {
    return undefined;
  }
  switch (relations.formats[relationId]) {
    case RELATION_FORMAT_CSR_UINT16:
      return readValue(payload, 0, 2);
    case RELATION_FORMAT_CSR_UINT32:
      return readValue(payload, 0, 4);
    case RELATION_FORMAT_ROARING:
      return roaringFirst(payload);
    default:
      throw new Error(`Unknown catalog relation encoding`);
  }
}

function relationPayload(relations: PackedRelations, relationId: number) {
  return relations.data.subarray(
    relations.offsets[relationId],
    relations.offsets[relationId + 1],
  );
}

function sortedBytesInclude(bytes: Uint8Array, width: 2 | 4, target: number) {
  let low = 0;
  let high = bytes.length / width - 1;
  while (low <= high) {
    const middle = low + Math.floor((high - low) / 2);
    const value = readValue(bytes, middle * width, width);
    if (value === target) return true;
    if (value < target) low = middle + 1;
    else high = middle - 1;
  }
  return false;
}

function readValue(bytes: Uint8Array, offset: number, width: 2 | 4) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return width === 2
    ? view.getUint16(offset, true)
    : view.getUint32(offset, true);
}

interface RoaringHeader {
  view: DataView;
  size: number;
  runBitmap?: Uint8Array;
  keysOffset: number;
  dataOffset: number;
  offsets?: number[];
}

// Reads the portable Roaring format emitted by the Go encoder without expanding it into product arrays.
function parseRoaring(bytes: Uint8Array): RoaringHeader {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const cookie = view.getUint32(0, true);
  let size: number;
  let cursor: number;
  let runBitmap: Uint8Array | undefined;
  if ((cookie & 0xffff) === 12347) {
    size = (cookie >>> 16) + 1;
    cursor = 4;
    const runBytes = Math.ceil(size / 8);
    runBitmap = bytes.subarray(cursor, cursor + runBytes);
    cursor += runBytes;
  } else if (cookie === 12346) {
    size = view.getUint32(4, true);
    cursor = 8;
  } else {
    throw new Error(`Invalid Roaring relation payload`);
  }

  const keysOffset = cursor;
  cursor += size * 4;
  let offsets: number[] | undefined;
  if (!runBitmap || size >= 4) {
    offsets = Array.from({ length: size }, (_, index) =>
      view.getUint32(cursor + index * 4, true),
    );
    cursor += size * 4;
  }
  return { view, size, runBitmap, keysOffset, dataOffset: cursor, offsets };
}

function roaringContains(bytes: Uint8Array, target: number) {
  const header = parseRoaring(bytes);
  const key = target >>> 16;
  const low = target & 0xffff;
  let dataCursor = header.dataOffset;
  for (let index = 0; index < header.size; index += 1) {
    const containerKey = header.view.getUint16(
      header.keysOffset + index * 4,
      true,
    );
    const cardinality =
      header.view.getUint16(header.keysOffset + index * 4 + 2, true) + 1;
    const isRun =
      !!header.runBitmap &&
      (header.runBitmap[Math.floor(index / 8)] & (1 << index % 8)) !== 0;
    const offset = header.offsets?.[index] ?? dataCursor;
    const size = roaringContainerSize(header.view, offset, cardinality, isRun);
    if (containerKey === key) {
      return roaringContainerContains(
        header.view,
        offset,
        cardinality,
        isRun,
        low,
      );
    }
    if (containerKey > key) return false;
    dataCursor += size;
  }
  return false;
}

function roaringFirst(bytes: Uint8Array) {
  const header = parseRoaring(bytes);
  if (!header.size) return undefined;
  const key = header.view.getUint16(header.keysOffset, true);
  const cardinality = header.view.getUint16(header.keysOffset + 2, true) + 1;
  const isRun = !!header.runBitmap && (header.runBitmap[0] & 1) !== 0;
  const offset = header.offsets?.[0] ?? header.dataOffset;
  if (isRun || cardinality <= 4096) {
    return key * 65_536 + header.view.getUint16(offset + (isRun ? 2 : 0), true);
  }
  for (let word = 0; word < 2048; word += 1) {
    const bits = header.view.getUint32(offset + word * 4, true);
    if (bits) return key * 65_536 + word * 32 + (31 - Math.clz32(bits & -bits));
  }
  return undefined;
}

function roaringContainerSize(
  view: DataView,
  offset: number,
  cardinality: number,
  isRun: boolean,
) {
  if (isRun) return 2 + view.getUint16(offset, true) * 4;
  return cardinality > 4096 ? 8192 : cardinality * 2;
}

function roaringContainerContains(
  view: DataView,
  offset: number,
  cardinality: number,
  isRun: boolean,
  target: number,
) {
  if (isRun) {
    const runs = view.getUint16(offset, true);
    for (let index = 0; index < runs; index += 1) {
      const start = view.getUint16(offset + 2 + index * 4, true);
      const length = view.getUint16(offset + 4 + index * 4, true);
      if (target >= start && target <= start + length) return true;
      if (target < start) return false;
    }
    return false;
  }
  if (cardinality <= 4096) {
    return sortedDataViewInclude(view, offset, cardinality, target);
  }
  const wordOffset = offset + Math.floor(target / 32) * 4;
  return (view.getUint32(wordOffset, true) & (1 << target % 32)) !== 0;
}

function sortedDataViewInclude(
  view: DataView,
  offset: number,
  count: number,
  target: number,
) {
  let low = 0;
  let high = count - 1;
  while (low <= high) {
    const middle = low + Math.floor((high - low) / 2);
    const value = view.getUint16(offset + middle * 2, true);
    if (value === target) return true;
    if (value < target) low = middle + 1;
    else high = middle - 1;
  }
  return false;
}
